import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  FlatList,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { useStrategyOrganizationsQuery, useOrganizationGoalsQuery } from '../../hooks/queries';
import { OrganizationList, Organization, StrategicGoalList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, PageContainer, Navbar, Breadcrumb, EmptyState } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { OrganizationCard } from './components';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { formatPct } from '../../utils/formatPercentage';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Back Arrow Icon
const ArrowRightIcon = ({ size = 24, color = '#111827' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Search Icon
const SearchIcon = ({ size = 20, color = '#9CA3AF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 21L16.65 16.65" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Plus Icon
const PlusIcon = ({ size = 20, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Building Icon
const BuildingIcon = ({ size = 24, color = '#A9211F' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 21H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 21V7L13 3V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 21V11L13 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Progress Circle Component
const ProgressCircle = ({
  percentage,
  size = 60,
  strokeWidth = 6
}: {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safePercentage = Math.min(Math.max(percentage, 0), 100);
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;

  const getColor = () => {
    if (safePercentage >= 80) return '#22C55E';
    if (safePercentage >= 50) return '#F97316';
    return '#EF4444';
  };

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getColor()}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </Svg>
      <Text style={[styles.progressText, { color: getColor() }]}>
        %{formatPct(safePercentage)}
      </Text>
    </View>
  );
};

// ChevronLeft Icon
const ChevronLeftIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Extended Organization type with additional fields
interface ExtendedOrganization extends OrganizationList {
  vision_achievement?: number;
  commitment_index?: number;
  kpis_count?: number;
}

export default function OrganizationsListScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    headerBg: colors.card,
    headerBorder: colors.cardBorder,
    headerTitle: colors.textPrimary,
    headerSubtitle: colors.textMuted,
    searchBg: isDark ? colors.backgroundAlt : '#F3F4F6',
    searchInput: colors.textPrimary,
    backButtonBg: isDark ? colors.backgroundAlt : '#F3F4F6',
    orgName: colors.textPrimary,
    statLabel: colors.textMuted,
    countContainer: isDark ? colors.backgroundAlt : '#F3F4F6',
    countValue: colors.textPrimary,
    goalsSectionBg: isDark ? colors.backgroundAlt : '#F9FAFB',
    goalsSectionTitle: colors.textSecondary,
    loadingText: colors.textMuted,
    goalCard: colors.card,
    goalTitle: colors.textPrimary,
    goalProgress: colors.textSecondary,
    iconContainer: isDark ? colors.primaryLight : '#FEF2F2',
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);

  // Organizations list — React Query cache keyed by year. Repeat visits
  // within 10min staleTime render from cache with no spinner flash.
  const {
    data: orgsData = [],
    isPending: loading,
    isRefetching: refreshing,
    refetch,
  } = useStrategyOrganizationsQuery(selectedYear);
  const organizations = orgsData as ExtendedOrganization[];

  // Goals for the expanded org. React Query caches per (orgId, year) for free
  // — collapse + re-expand the same org resolves from cache, no HTTP call.
  const {
    data: orgGoalsData = [],
    isPending: goalsQueryPending,
    isFetching: goalsQueryFetching,
  } = useOrganizationGoalsQuery(selectedOrgId, selectedYear);
  const orgGoals = selectedOrgId ? orgGoalsData : [];
  // Only show the goals spinner when we're actually fetching (not just
  // waiting for `enabled` to flip). `isPending` is true while disabled so
  // we gate on isFetching + a selected org.
  const loadingGoals = selectedOrgId != null && goalsQueryFetching && goalsQueryPending;

  const onRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleSelectOrg = useCallback((orgId: number) => {
    setSelectedOrgId(prev => (prev === orgId ? null : orgId));
  }, []);

  const handleCreateGoal = (orgId: number) => {
    navigation.navigate('AddGoal', { organizationId: orgId });
  };

  const handleViewGoal = (goalId: number) => {
    navigation.navigate('GoalDetail', { id: goalId });
  };

  const handleEditGoal = (goalId: number) => {
    navigation.navigate('AddGoal', { goalId });
  };

  const filteredOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: 'الرئيسية' },
          { label: 'الجهات الحكومية' },
        ]}
      />

      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>الجهات الحكومية</Text>
          <Text style={styles.headerSubtitle}>
            {organizations.length} جهة حكومية
          </Text>
        </View>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <ArrowRightIcon size={24} color="#111827" />
        </TouchableOpacity>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <SearchIcon size={20} color="#9CA3AF" />
          <TextInput
            style={styles.searchInput}
            placeholder="البحث عن جهة..."
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>
      </View>

      {/* Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {filteredOrganizations.length === 0 ? (
          <EmptyState
            title="لا توجد جهات"
            message={searchQuery ? 'لم يتم العثور على نتائج للبحث' : 'لم يتم إضافة أي جهات بعد'}
          />
        ) : (
          filteredOrganizations.map((org) => (
            <View key={org.id}>
              {/* Organization Card */}
              <TouchableOpacity
                style={[styles.orgCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                onPress={() => handleSelectOrg(org.id)}
                activeOpacity={0.7}
              >
                {/* Header */}
                <View style={styles.orgHeader}>
                  <View style={[styles.orgIconContainer, { backgroundColor: themeColors.iconContainer }]}>
                    <BuildingIcon size={24} color={colors.primary} />
                  </View>
                  <View style={styles.orgHeaderContent}>
                    <Text style={[styles.orgName, { color: themeColors.orgName }]} numberOfLines={2}>{translateOrg(org.name)}</Text>
                  </View>
                  <ChevronLeftIcon
                    size={20}
                    color={selectedOrgId === org.id ? colors.primary : colors.iconDefault}
                  />
                </View>

                {/* Stats Row */}
                <View style={[styles.statsRow, { borderTopColor: colors.border }]}>
                  {/* Vision Achievement */}
                  <View style={styles.statItem}>
                    <ProgressCircle percentage={Number(((org.stage_actual_progress ?? 0) * 100).toFixed(2))} size={56} strokeWidth={5} />
                    <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>تحقيق الرؤية</Text>
                  </View>

                  {/* Commitment Index */}
                  <View style={styles.statItem}>
                    <ProgressCircle percentage={org.commitment_index ?? 0} size={56} strokeWidth={5} />
                    <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>مؤشر الالتزام</Text>
                  </View>

                  {/* Goals Count */}
                  <View style={styles.statItem}>
                    <View style={[styles.countContainer, { backgroundColor: themeColors.countContainer }]}>
                      <Text style={[styles.countValue, { color: themeColors.countValue }]}>{org.strategic_goals_count ?? 0}</Text>
                    </View>
                    <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>الأهداف</Text>
                  </View>

                  {/* KPIs Count */}
                  <View style={styles.statItem}>
                    <View style={[styles.countContainer, { backgroundColor: themeColors.countContainer }]}>
                      <Text style={[styles.countValue, { color: themeColors.countValue }]}>{org.kpis_count ?? 0}</Text>
                    </View>
                    <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>المؤشرات</Text>
                  </View>
                </View>
              </TouchableOpacity>

              {/* Expanded Goals Section */}
              {selectedOrgId === org.id && (
                <View style={[styles.goalsSection, { backgroundColor: themeColors.goalsSectionBg, borderColor: themeColors.cardBorder }]}>
                  <View style={styles.goalsSectionHeader}>
                    <TouchableOpacity
                      style={styles.addGoalButton}
                      onPress={() => handleCreateGoal(org.id)}
                    >
                      <PlusIcon size={16} color="#FFFFFF" />
                      <Text style={styles.addGoalButtonText}>إضافة هدف</Text>
                    </TouchableOpacity>
                    <Text style={[styles.goalsSectionTitle, { color: themeColors.goalsSectionTitle }]}>
                      أهداف الجهة ({orgGoals.length})
                    </Text>
                  </View>

                  {loadingGoals ? (
                    <View style={styles.loadingContainer}>
                      <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>جاري التحميل...</Text>
                    </View>
                  ) : orgGoals.length === 0 ? (
                    <View style={[styles.noGoalsContainer, { backgroundColor: themeColors.goalCard }]}>
                      <Text style={[styles.noGoalsText, { color: themeColors.loadingText }]}>لا توجد أهداف لهذه الجهة</Text>
                    </View>
                  ) : (
                    orgGoals.map((goal) => (
                      <View key={goal.id} style={[styles.goalCard, { backgroundColor: themeColors.goalCard, borderColor: themeColors.cardBorder }]}>
                        <View style={styles.goalContent}>
                          <Text style={[styles.goalTitle, { color: themeColors.goalTitle }]} numberOfLines={2}>
                            {goal.title}
                          </Text>
                          <View style={styles.goalMeta}>
                            <View style={[
                              styles.goalStatus,
                              goal.status === 'approved' && styles.goalStatusApproved,
                              goal.status === 'pending' && styles.goalStatusPending,
                              goal.status === 'rejected' && styles.goalStatusRejected,
                            ]}>
                              <Text style={[
                                styles.goalStatusText,
                                goal.status === 'approved' && styles.goalStatusTextApproved,
                                goal.status === 'pending' && styles.goalStatusTextPending,
                                goal.status === 'rejected' && styles.goalStatusTextRejected,
                              ]}>
                                {goal.status_display ?? goal.status}
                              </Text>
                            </View>
                            <Text style={styles.goalProgress}>
                              %{formatPct(goal.achievement_percentage ?? 0)}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.goalActions}>
                          <TouchableOpacity
                            style={styles.goalActionButton}
                            onPress={() => handleViewGoal(goal.id)}
                          >
                            <Text style={styles.goalActionText}>عرض</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[styles.goalActionButton, styles.goalEditButton]}
                            onPress={() => handleEditGoal(goal.id)}
                          >
                            <Text style={styles.goalEditText}>تعديل</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))
                  )}
                </View>
              )}
            </View>
          ))
        )}
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  backButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
  },
  headerContent: {
    flex: 1,
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    color: '#111827',
    textAlign: 'right',
  },
  headerSubtitle: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    marginTop: 2,
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 44,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#111827',
    textAlign: 'right',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  orgCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  orgHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  orgIconContainer: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: '#FEF2F2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  orgHeaderContent: {
    flex: 1,
    marginRight: 12,
    marginLeft: 8,
  },
  orgName: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: '#111827',
    textAlign: 'right',
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginTop: 6,
    textAlign: 'center',
  },
  progressText: {
    position: 'absolute',
    fontSize: 12,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
  },
  countContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  countValue: {
    fontSize: 18,
    fontFamily: FONTS.numbers,
    fontWeight: 'bold',
    color: '#374151',
  },
  goalsSection: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    marginTop: -8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  goalsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  goalsSectionTitle: {
    fontSize: 15,
    fontFamily: FONTS.bold,
    color: '#374151',
  },
  addGoalButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#A9211F',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 6,
  },
  addGoalButtonText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  loadingContainer: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  noGoalsContainer: {
    paddingVertical: 24,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
  },
  noGoalsText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  goalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  goalContent: {
    flex: 1,
    marginRight: 12,
  },
  goalTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#111827',
    textAlign: 'right',
    marginBottom: 6,
  },
  goalMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  goalStatus: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
  },
  goalStatusApproved: {
    backgroundColor: '#DCFCE7',
  },
  goalStatusPending: {
    backgroundColor: '#FEF3C7',
  },
  goalStatusRejected: {
    backgroundColor: '#FEF2F2',
  },
  goalStatusText: {
    fontSize: 10,
    fontFamily: FONTS.medium,
    color: '#6B7280',
  },
  goalStatusTextApproved: {
    color: '#16A34A',
  },
  goalStatusTextPending: {
    color: '#D97706',
  },
  goalStatusTextRejected: {
    color: '#DC2626',
  },
  goalProgress: {
    fontSize: 12,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    color: '#374151',
  },
  goalActions: {
    flexDirection: 'row',
    gap: 6,
  },
  goalActionButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: '#F3F4F6',
  },
  goalEditButton: {
    backgroundColor: '#EFF6FF',
  },
  goalActionText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#374151',
  },
  goalEditText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#2563EB',
  },
});
